/**
 * MEVIA - Identity Profiles Management (Super Admin)
 * Phase 4: Manage company identity profiles and generate custom profiles
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  ArrowLeft,
  RefreshCw,
  Building2,
  Eye,
  CheckCircle,
  Clock,
  FileText,
  AlertCircle,
  Settings,
  Save,
  Target,
  Heart,
  Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

// Status configuration
const STATUS_CONFIG = {
  draft: { es: 'Borrador', en: 'Draft', color: 'bg-gray-100 text-gray-800' },
  submitted: { es: 'Pendiente', en: 'Pending', color: 'bg-amber-100 text-amber-800' },
  active: { es: 'Activo', en: 'Active', color: 'bg-green-100 text-green-800' },
  inactive: { es: 'Inactivo', en: 'Inactive', color: 'bg-gray-100 text-gray-600' }
};

const IdentityProfilesManagement = ({ onBack }) => {
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [allProfiles, setAllProfiles] = useState([]);
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Form state for generated profile
  const [generatedProfile, setGeneratedProfile] = useState({
    profile_data: {},
    admin_notes: ''
  });

  // Translations
  const t = {
    title: language === 'es' ? 'Perfiles de Identidad Corporativa' : 'Corporate Identity Profiles',
    subtitle: language === 'es' 
      ? 'Gestiona los perfiles de identidad y genera perfiles culturales personalizados'
      : 'Manage identity profiles and generate custom cultural profiles',
    back: language === 'es' ? 'Volver' : 'Back',
    refresh: language === 'es' ? 'Actualizar' : 'Refresh',
    pending: language === 'es' ? 'Pendientes' : 'Pending',
    all: language === 'es' ? 'Todos' : 'All',
    company: language === 'es' ? 'Empresa' : 'Company',
    status: language === 'es' ? 'Estado' : 'Status',
    submittedAt: language === 'es' ? 'Enviado' : 'Submitted',
    enabled: language === 'es' ? 'Habilitado' : 'Enabled',
    actions: language === 'es' ? 'Acciones' : 'Actions',
    view: language === 'es' ? 'Ver Detalle' : 'View Details',
    generate: language === 'es' ? 'Generar Perfil' : 'Generate Profile',
    noPending: language === 'es' ? 'No hay perfiles pendientes' : 'No pending profiles',
    noProfiles: language === 'es' ? 'No hay perfiles de identidad' : 'No identity profiles',
    detailTitle: language === 'es' ? 'Detalle de Identidad' : 'Identity Details',
    mission: language === 'es' ? 'Misión' : 'Mission',
    vision: language === 'es' ? 'Visión' : 'Vision',
    values: language === 'es' ? 'Valores' : 'Values',
    optionalBlocks: language === 'es' ? 'Bloques Adicionales' : 'Additional Blocks',
    notesFromCompany: language === 'es' ? 'Notas de la Empresa' : 'Notes from Company',
    generateTitle: language === 'es' ? 'Generar Perfil Cultural' : 'Generate Cultural Profile',
    generateDesc: language === 'es'
      ? 'Configura el perfil cultural basado en la identidad corporativa de la empresa'
      : 'Configure the cultural profile based on the company\'s corporate identity',
    profileData: language === 'es' ? 'Datos del Perfil (JSON)' : 'Profile Data (JSON)',
    profileDataDesc: language === 'es'
      ? 'Ingresa los datos del perfil en formato JSON'
      : 'Enter the profile data in JSON format',
    adminNotes: language === 'es' ? 'Notas del Administrador' : 'Admin Notes',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    saveAndActivate: language === 'es' ? 'Guardar y Activar' : 'Save and Activate',
    saving: language === 'es' ? 'Guardando...' : 'Saving...',
    settings: language === 'es' ? 'Configuración' : 'Settings',
    enableProfile: language === 'es' ? 'Habilitar Perfil por Identidad' : 'Enable Identity Profile',
    enableProfileDesc: language === 'es'
      ? 'Al habilitar, se usará el perfil personalizado y se ocultará la evaluación por tokens'
      : 'When enabled, the custom profile will be used and token assessment will be hidden',
    savedSuccess: language === 'es' ? 'Perfil generado y activado' : 'Profile generated and activated',
    settingsUpdated: language === 'es' ? 'Configuración actualizada' : 'Settings updated',
    error: language === 'es' ? 'Error al procesar la solicitud' : 'Error processing request',
    close: language === 'es' ? 'Cerrar' : 'Close',
    yes: language === 'es' ? 'Sí' : 'Yes',
    no: language === 'es' ? 'No' : 'No'
  };

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const [allRes, pendingRes] = await Promise.all([
        axios.get('/company-identity/admin/all'),
        axios.get('/company-identity/admin/pending')
      ]);
      setAllProfiles(allRes.data);
      setPendingProfiles(pendingRes.data);
    } catch (error) {
      logger.error('Error loading profiles:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  }, [t.error]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const loadProfileDetail = async (empresaId) => {
    try {
      const response = await axios.get(`/company-identity/admin/${empresaId}`);
      setSelectedProfile(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      logger.error('Error loading profile:', error);
      toast.error(t.error);
    }
  };

  const openGenerateDialog = async (empresaId) => {
    try {
      const response = await axios.get(`/company-identity/admin/${empresaId}`);
      setSelectedProfile(response.data);
      setGeneratedProfile({
        profile_data: response.data.generated_profile || {},
        admin_notes: response.data.admin_notes || ''
      });
      setShowGenerateDialog(true);
    } catch (error) {
      logger.error('Error loading profile:', error);
      toast.error(t.error);
    }
  };

  const handleSaveGeneratedProfile = async () => {
    try {
      setSaving(true);
      
      // Parse profile data if it's a string
      let profileData = generatedProfile.profile_data;
      if (typeof profileData === 'string') {
        profileData = JSON.parse(profileData);
      }
      
      await axios.post(`/company-identity/admin/${selectedProfile.empresa_id}/generate`, {
        profile_data: profileData,
        admin_notes: generatedProfile.admin_notes
      });
      
      toast.success(t.savedSuccess);
      setShowGenerateDialog(false);
      loadProfiles();
    } catch (error) {
      logger.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || t.error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (empresaId, currentValue) => {
    try {
      await axios.put(`/company-identity/admin/${empresaId}/settings`, {
        identity_profile_enabled: !currentValue
      });
      toast.success(t.settingsUpdated);
      loadProfiles();
    } catch (error) {
      logger.error('Error updating settings:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-purple-600" />
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadProfiles}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t.refresh}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingProfiles.length}</p>
                <p className="text-sm text-gray-500">{t.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {allProfiles.filter(p => p.status === 'active').length}
                </p>
                <p className="text-sm text-gray-500">{STATUS_CONFIG.active[language]}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allProfiles.length}</p>
                <p className="text-sm text-gray-500">{t.all}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t.pending} ({pendingProfiles.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t.all}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pendingProfiles.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-4" />
                <p className="text-gray-500">{t.noPending}</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.company}</TableHead>
                      <TableHead>{t.values}</TableHead>
                      <TableHead>{t.submittedAt}</TableHead>
                      <TableHead className="text-right">{t.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingProfiles.map((profile) => (
                      <TableRow key={profile.id}>
                        <TableCell>
                          <div className="font-medium">{profile.empresa_name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {profile.mission}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{profile.values_count} valores</Badge>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {formatDate(profile.submitted_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => loadProfileDetail(profile.empresa_id)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              {t.view}
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => openGenerateDialog(profile.empresa_id)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              {t.generate}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all">
          {allProfiles.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">{t.noProfiles}</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.company}</TableHead>
                      <TableHead>{t.status}</TableHead>
                      <TableHead>{t.enabled}</TableHead>
                      <TableHead>{t.submittedAt}</TableHead>
                      <TableHead className="text-right">{t.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allProfiles.map((profile) => (
                      <TableRow key={profile.id}>
                        <TableCell className="font-medium">
                          {profile.empresa_name}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_CONFIG[profile.status]?.color}>
                            {STATUS_CONFIG[profile.status]?.[language]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={profile.identity_profile_enabled}
                            onCheckedChange={() => handleToggleEnabled(profile.empresa_id, profile.identity_profile_enabled)}
                            disabled={profile.status !== 'active'}
                          />
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {formatDate(profile.submitted_at || profile.updated_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => loadProfileDetail(profile.empresa_id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {profile.status === 'submitted' && (
                              <Button 
                                size="sm"
                                onClick={() => openGenerateDialog(profile.empresa_id)}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedProfile?.empresa_name}
            </DialogTitle>
            <DialogDescription>{t.detailTitle}</DialogDescription>
          </DialogHeader>
          
          {selectedProfile && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Badge className={STATUS_CONFIG[selectedProfile.status]?.color}>
                  {STATUS_CONFIG[selectedProfile.status]?.[language]}
                </Badge>
                {selectedProfile.identity_profile_enabled && (
                  <Badge className="bg-teal-100 text-teal-800">{t.enabled}</Badge>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-teal-600" />
                    <Label className="font-semibold">{t.mission}</Label>
                  </div>
                  <p className="text-gray-700">{selectedProfile.mission}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="h-4 w-4 text-purple-600" />
                    <Label className="font-semibold">{t.vision}</Label>
                  </div>
                  <p className="text-gray-700">{selectedProfile.vision}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-red-500" />
                    <Label className="font-semibold">{t.values}</Label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.values?.map((value, i) => (
                      <Badge key={i} variant="outline">{value}</Badge>
                    ))}
                  </div>
                </div>
                
                {selectedProfile.optional_blocks?.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      <Label className="font-semibold">{t.optionalBlocks}</Label>
                    </div>
                    <div className="space-y-3">
                      {selectedProfile.optional_blocks.map((block, i) => (
                        <div key={i} className="border-l-2 border-amber-300 pl-3">
                          <p className="font-medium">{block.title}</p>
                          <p className="text-sm text-gray-600">{block.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedProfile.notes_for_support && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <Label className="font-semibold">{t.notesFromCompany}</Label>
                    </div>
                    <p className="text-amber-800">{selectedProfile.notes_for_support}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              {t.close}
            </Button>
            {selectedProfile?.status === 'submitted' && (
              <Button 
                onClick={() => {
                  setShowDetailDialog(false);
                  openGenerateDialog(selectedProfile.empresa_id);
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Settings className="h-4 w-4 mr-2" />
                {t.generate}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Profile Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t.generateTitle}</DialogTitle>
            <DialogDescription>
              {selectedProfile?.empresa_name} - {t.generateDesc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t.profileData}</Label>
              <p className="text-sm text-gray-500 mb-2">{t.profileDataDesc}</p>
              <Textarea
                value={typeof generatedProfile.profile_data === 'string' 
                  ? generatedProfile.profile_data 
                  : JSON.stringify(generatedProfile.profile_data, null, 2)}
                onChange={(e) => setGeneratedProfile(prev => ({
                  ...prev,
                  profile_data: e.target.value
                }))}
                className="font-mono text-sm min-h-[200px]"
                placeholder='{"perfil_ideal": {...}, "indicadores_objetivo": {...}}'
              />
            </div>
            
            <div>
              <Label>{t.adminNotes}</Label>
              <Textarea
                value={generatedProfile.admin_notes}
                onChange={(e) => setGeneratedProfile(prev => ({
                  ...prev,
                  admin_notes: e.target.value
                }))}
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleSaveGeneratedProfile}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? t.saving : t.saveAndActivate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdentityProfilesManagement;
